// src/components/TopBar/TopBar.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

const TopBar = ({ activeButton }) => {
  const navigate = useNavigate();

  // === MENU BAR POSITIONING CONTROLS ===
  // Spacing everything perfectly at 10% increments across the screen
  const btnLeftAlignX = 'left';
  const btnLeftDistanceX = '10%'; // 10% from the left

  const btnMidStartX = 20; // Starts at 20%
  const btnMidSpacingX = 10; // Spaces evenly by 10% each

  const btnRightAlignX = 'right';
  const btnRightDistanceX = '10%'; // 10% from the right (which is 90% from the left)

  const btnPositionY = '36px'; // Reduced by 40% (was 60px)

  // === NAVIGATION CONFIGURATION ===
  const midButtons = [
    { id: 'about', label: 'About', route: '/about' }, // 20%
    { id: 'overview', label: 'Overview', route: '/overview' }, // 30%
    { id: 'client-view', label: 'Client View', route: '/client-view' }, // 40%
    { id: 'view-info', label: 'View\nInfo', route: '/view-info' }, // 50% (Perfectly Centered)
    { id: 'update-markers', label: 'Update Markers', route: '/update-markers' }, // 60%
    { id: 'promo', label: 'Promo', route: '/promo' }, // 70%
    { id: 'admin', label: 'Admin', route: '/admin' }, // 80%
  ];

  return (
    <>
      {/* 1. LEFT BUTTON (Home) */}
      <Box
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          [btnLeftAlignX]: btnLeftDistanceX,
          top: btnPositionY,
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
          textAlign: 'center',
          cursor: 'pointer',
          width: '90px', // Safari transform fix + boundary for wrapping
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '18px',
            lineHeight: 1.2, // For tight double lines
            fontWeight: activeButton === 'home' ? 700 : 500,
            color: '#000000',
            whiteSpace: 'pre-line', // <-- Changed to pre-line to respect \n
            wordWrap: 'break-word',
            transition: 'color 0.2s ease',
            position: 'relative',
            // === HORIZONTAL BLACK LINE ===
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 'calc(100% + 4px)', // Positioned securely 4px below the text, respects multiple lines
              left: '50%',
              transform: 'translateX(-50%)',
              width: activeButton === 'home' ? '100px' : '0px', // Length of the bar
              height: '5px', // Thickness/Width of the bar
              backgroundColor: '#000000',
              transition: 'width 0.2s ease', // Smooth animation
            },
            '&:hover': {
              color: '#87CEEB', // Retained text hover color
            },
            '&:hover::after': {
              width: activeButton === 'home' ? '110px' : '0px', // Increases active line length by 10%
            },
          }}>
          Home
        </Typography>
      </Box>

      {/* 2. THE 7 INTERMEDIATE NAV BUTTONS */}
      {midButtons.map((btn, index) => {
        const posX = `${btnMidStartX + index * btnMidSpacingX}%`;
        const isActive = activeButton === btn.id;

        return (
          <Box
            key={btn.id}
            onClick={() => navigate(btn.route)}
            sx={{
              position: 'absolute',
              left: posX,
              top: btnPositionY,
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
              cursor: 'pointer',
              width: '90px', // Safari transform fix + boundary for wrapping
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '18px',
                lineHeight: 1.2, // For tight double lines
                fontWeight: isActive ? 700 : 500,
                color: '#000000',
                whiteSpace: 'pre-line', // <-- Changed to pre-line to respect \n
                wordWrap: 'break-word',
                transition: 'color 0.2s ease',
                position: 'relative',
                // === HORIZONTAL BLACK LINE ===
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 'calc(100% + 4px)', // Positioned securely 4px below the text, respects multiple lines
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: isActive ? '100px' : '0px', // Length of the bar
                  height: '5px', // Thickness/Width of the bar
                  backgroundColor: '#000000',
                  transition: 'width 0.2s ease', // Smooth animation
                },
                '&:hover': {
                  color: '#87CEEB', // Retained text hover color
                },
                '&:hover::after': {
                  width: isActive ? '110px' : '0px', // Increases active line length by 10%
                },
              }}>
              {btn.label}
            </Typography>
          </Box>
        );
      })}

      {/* 3. RIGHT BUTTON (Demo) */}
      <Box
        onClick={() => navigate('/demo')}
        sx={{
          position: 'absolute',
          [btnRightAlignX]: btnRightDistanceX,
          top: btnPositionY,
          transform: 'translate(50%, -50%)',
          zIndex: 10,
          textAlign: 'center',
          cursor: 'pointer',
          width: '90px', // Safari transform fix + boundary for wrapping
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '18px',
            lineHeight: 1.2, // For tight double lines
            fontWeight: activeButton === 'demo' ? 700 : 500,
            color: '#000000',
            whiteSpace: 'pre-line', // <-- Changed to pre-line to respect \n
            wordWrap: 'break-word',
            transition: 'color 0.2s ease',
            position: 'relative',
            // === HORIZONTAL BLACK LINE ===
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 'calc(100% + 4px)', // Positioned securely 4px below the text, respects multiple lines
              left: '50%',
              transform: 'translateX(-50%)',
              width: activeButton === 'demo' ? '100px' : '0px', // Length of the bar
              height: '5px', // Thickness/Width of the bar
              backgroundColor: '#000000',
              transition: 'width 0.2s ease', // Smooth animation
            },
            '&:hover': {
              color: '#87CEEB', // Retained text hover color
            },
            '&:hover::after': {
              width: activeButton === 'demo' ? '110px' : '0px', // Increases active line length by 10%
            },
          }}>
          Demo
        </Typography>
      </Box>
    </>
  );
};

export default TopBar;
